import axios from "axios"
import { buildAuthIdentifierBody } from '../utils/authUtils';
import { getData, getErrorMessage, isSuccess } from "../utils/apiResponse"
// import { API_URL } from '../../config'
const API_URL = process.env.REACT_APP_API_URL;
// const API_URL = 'http://localhost:5000/api'
//const TWILIO_ACCOUNT_SID = process.env.REACT_APP_TWILIO_ACCOUNT_SID
//const TWILIO_AUTH_TOKEN = process.env.REACT_APP_TWILIO_TOKEN
//const twilioClient = twilio(TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN)

/**
 * Fetches user profile data by ID.
 * @param {string} id - User ID
 * @param {string} token - JWT token for authentication
 * @returns {Object|null} User data or null on error
 */
export const getUser = async (id,token) => {
    try {
        const response = await axios.get(`${API_URL}/users/${id}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        })
        if (response.status >= 200 && response.status < 300) {
            const result = response.data.data
            // console.log('getUser response: ',result)
            return result
        }
        return null
    } catch (e) {
        console.error('Error getting user profile: ', e)
        return null
    }
}

/**
 * Updates user profile information.
 * @param {Object} obj - User profile data object with id and updated fields
 * @param {string} token - User authentication token
 * @returns {Object} Object with success boolean
 */
export const editProfile = async (obj,token) => {
    try {
        const response = await axios.put(`${API_URL}/users/${obj?.id}`, obj, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        })
        
        if (response.status >= 200 && response.status < 300) {
            // console.log('editProfile response: ',response)
            return { success: true }
        }
        return { success: false }
    } catch (e) {
        console.error('Error editing profile: ',e)
        // Forward the backend's specific message (e.g. "This phoneNumber is
        // already in use by another account") instead of discarding it --
        // the caller previously had no way to show anything but a generic
        // fallback regardless of the actual reason it failed.
        return { success: false, message: e.response?.data?.message }
    }
}

/**
 * Uploads a profile picture for the user.
 * @param {FormData} formData - Form data containing the image file
 * @param {string} token - User authentication token
 * @returns {Object} Object with success boolean and profile picture URL
 */
export const uploadProfilePicture = async (formData, token) => {
    try {
        const response = await axios.post(`${API_URL}/users/me/profile-picture`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        })
        // console.log('response from uploadProfilePicture: ', response)
        if (response.status >= 200 && response.status < 300) {
            return { success: true, url: response.data.data.profilePictureUrl }
        }
        return { success: false, error: "Failed to upload image" }
    } catch (e) {
        console.error('Error editing profile: ',e)
        return { success: false, error: "Failed to upload image"}
    }
}

/**
 * Authenticates a user with email or phone number and password.
 * Automatically detects if identifier is email or phone based on '@' symbol.
 * @param {Object} obj - Login credentials with identifier and password
 * @returns {Object|null} User authentication data or null on error
 */
export const authenticateUser = async (obj) => {
    const identifier = (obj.identifier || obj.phoneNumber || obj.email || '').trim();
    const body = {
        ...buildAuthIdentifierBody(identifier),
        password: obj.password,
        // Was previously silently dropped here -- Login.js checked it
        // client-side but this function never forwarded it, so the
        // backend never received a token to verify against Google.
        ...(obj.captchaToken ? { captchaToken: obj.captchaToken } : {}),
    };
    try {
        const response = await axios.post(`${API_URL}/auth/login`, body, {
            headers: { "X-Client-Platform": "web" },
        });
        // The backend returns HTTP 200 with success:false for this case
        // (not a distinct status code), so this must be checked before the
        // generic success check below, which would otherwise treat it as
        // a plain failure and return null.
        if (response.data?.requiresVerification) {
            return {
                requiresVerification: true,
                message: response.data.message,
                ...response.data.data,
            };
        }
        if (response.status >= 200 && response.status < 300) {
            if (response.data?.errors || !response.data?.success) {
                return null;
            }
            return response.data.data;
        }
        return null;
    } catch (err) {
        throw err;
    }
}

/**
 * Verifies user identity with email/phone, password, and order ID.
 * Used for account verification during operations.
 * @param {Object} obj - Verification data with identifier, password, and orderId
 * @returns {Object|null} Verification data or null on error
 */
export const verifyUser = async(obj) => {
    const body = {
        ...buildAuthIdentifierBody(obj.identifier),
        password: obj.password,
        orderId: obj.orderId,
    };

    const response = await axios.post(`${API_URL}/auth/verify-email`, body)
    if (response.status >= 200 && response.status < 300) {
        // console.log('verify user response: ', response)
        if (response.data?.errors) {
            return null
        }
        return response.data
    }
    return null
}

/**
 * Registers a new user with signup information.
 * @param {Object} obj - User registration data
 * @returns {Object} Object with success boolean and access token
 */
export const registerUser = async (obj) => {
    try {
        const response = await axios.post(`${API_URL}/auth/register`, obj)
        if (response.data?.requiresVerification) {
            return {
                success: true,
                requiresVerification: true,
                token: response.data.data?.accessToken,
                user: response.data.data?.user,
                message: response.data.message,
            };
        }
        if (response.status >= 200 && response.status < 300) {
            if (response.data.error || !response.data?.success) {
                return { success: false, error: response.data.message}
            }
            return {
                success: true,
                token: response.data.data.accessToken,
                user: response.data.data.user,
            }
        }
    } catch (err) {
        // Per Asnake: the backend returns both a generic top-level
        // `message` ("Password validation failed") and a specific
        // `errors` array ("Password must be at least 8 characters
        // long", etc.) -- message is always truthy, so it was always
        // shadowing errors via ||, and the array never reached this
        // component's own (already-correct) array-handling logic below.
        const specificErrors = err.response?.data?.errors;
        return {
            success: false,
            error: Array.isArray(specificErrors) && specificErrors.length > 0
                ? specificErrors
                : (err.response?.data?.message || err.message),
        }
    }
}

export const verifyAccountRegistration = async (obj, token) => {
    try {
        // New backend expects { email, otp } on /auth/verify-email (no auth header needed)
        const email = obj.email || (token ? JSON.parse(atob(token.split('.')[1]))?.email : null);
        const response = await axios.post(`${API_URL}/auth/verify-email`, {
            email,
            otp: obj.otp,
        })
        if (response.status >= 200 && response.status < 300) {
            if (response.data.error || !response.data?.success) {
                return { success: false, error: response.data.message}
            }
            return { success: true}
        }
        return { success: false}
    } catch (err) {
        console.error('error verifying account: ',err)
        return { success: false, error: err.message}
    }
}

// For phone-based signups specifically -- verifyAccountRegistration above
// always calls /auth/verify-email and needs an email pulled from the JWT,
// which a phone-only signup never has. This hits the matching /auth/verify-phone
// endpoint instead, which only needs { phoneNumber, otp }.
export const verifyPhoneRegistration = async (phoneNumber, otp) => {
    try {
        const response = await axios.post(`${API_URL}/auth/verify-phone`, {
            phoneNumber,
            otp,
        })
        if (response.status >= 200 && response.status < 300) {
            if (response.data.error || !response.data?.success) {
                return { success: false, error: response.data.message}
            }
            return { success: true}
        }
        return { success: false}
    } catch (err) {
        console.error('error verifying phone: ', err)
        return { success: false, error: err.response?.data?.message || err.message}
    }
}
export const resendVerificationOtp = async (token) => {
    const response = await axios.post(`${API_URL}/auth/resend-verification-otp`, {}, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'accept': '*/*'
        }
    })
    if (response.status >= 200 && response.status < 300) {
        return { success: true}
    } else if (response.status == 400) {
        return { success: false, message: "Account is already verified"}
    } else if (response.status == 429) {
        return { success: false, message: 'Exceeding hourly request limit (max 3)'}
    } else {    
        return { success: false, message: "Could not identify user"}
    }
}

export const forgotPassword = async (obj) => {
    try {
        let body;
        if (obj.email) {
            body = { email: obj.email.trim() };
        } else if (obj.phoneNumber) {
            body = { phoneNumber: obj.phoneNumber.trim() };
        } else if (obj.identifier) {
            body = buildAuthIdentifierBody(obj.identifier);
        } else if (obj.phonenumber) {
            body = { phoneNumber: obj.phonenumber.trim() };
        } else {
            return { success: false };
        }

        const response = await axios.post(`${API_URL}/auth/forgot-password`, body)
        if (response.status >= 200 && response.status < 300) {
            // console.log('forgot password response: ', response)
            if (response.data.error) {
                return { success: false}
            }
            return { success: true}
        }
        return { success: false}
    } catch (err) {
        console.error('Error initializing reset password: ',err)
        return { success: false}
    }
}

export const resetPassword = async (obj) => {
    try {
        const response = await axios.post(`${API_URL}/auth/reset-password`, obj)
        if (response.status >= 200 && response.status < 300) {
            if (response.data.error) {
                return { success: false, error: response.data.message }
            }
            return { success: true}
        }
        return { success: false, error: response.data?.message }
    } catch (err) {
        console.error('Error resetting password: ',err)
        // Per Asnake: this previously discarded the backend's message and
        // errors array entirely, so PasswordReset.jsx had nothing to show
        // beyond a static generic "failed" string regardless of why.
        const specificErrors = err.response?.data?.errors;
        return {
            success: false,
            error: Array.isArray(specificErrors) && specificErrors.length > 0
                ? specificErrors
                : (err.response?.data?.message || err.message),
        }
    }
}

export const verifyOTP = async (obj) => {
    try {
        let body;
        if (obj.email) {
            body = { email: obj.email.trim(), otp: obj.otp };
        } else if (obj.phoneNumber) {
            body = { phoneNumber: obj.phoneNumber.trim(), otp: obj.otp };
        } else if (obj.identifier) {
            body = { ...buildAuthIdentifierBody(obj.identifier), otp: obj.otp };
        } else if (obj.phonenumber) {
            body = { phoneNumber: obj.phonenumber.trim(), otp: obj.otp };
        } else {
            return { success: false };
        }

        const response = await axios.post(`${API_URL}/auth/verify-otp`, body)
        if (response.status >= 200 && response.status < 300) {
            if (response.data.error || !response.data?.success) {
                return { success: false }
            }
            return {
                success: true,
                resetToken: response.data.resetToken,
                data: response.data,
            }
        }
        return { success: false}
    } catch (err) {
        console.error("Error validating OTP: ",err)
        return { success: false, message: err.response?.data?.message }
    }
}
//TWILIO

// export const twilioSendToken = async (formData) => {
//     console.log('number received: ', formData.phonenumber)
//     try {
//         const verification = await twilioClient.verify.v2.services("")
//         .verifications.create({
//             channel: "sms",
//             to: formData.phonenumber,
//         })

//         console.log('twilio verification status: ', verification.status)

//     } catch (err) {
//         console.error('Error sending twilio token: ',err)
//         return { success: false, error: err.message }
//     }
// }

// export const twilioVerifyToken = async (token) => {
//     try {

//     } catch (err) {
//         console.error('Error verifying twilio token: ',err)
//         return { success: false, error: err.message }
//     }
//}

export const twilioPhoneRegister = async (phonenumber, countryId) => {
    try {   
        const response = await axios.post(`${API_URL}/auth/phone-register-otp`, {countryId: countryId, phoneNumber: phonenumber})
        if (response.status >= 200 && response.status < 300) {
            // console.log('sent twilio verification code response: ',response)
            return {success: true}
        } else {
            return { success: false}
        }
    } catch (err) {
        console.error('Error sending twilio verification token: ',err)
        return { success: false }
    }
}

export const twilioVerifyPhoneRegister = async (obj) => {
    try {
        const response = await axios.post(`${API_URL}/auth/verify-phone-register-otp`, obj)
        if (response.status >= 200 && response.status < 300) {
            if (response.data?.error || !response.data?.success) {
                return { success: false, error: response.data?.message }
            }
            return { success: true, data: response.data.data }
        }
        return { success: false}
    } catch (err) {
        console.error('Error verifying phone number signup:',err)
        // Per Asnake: was only ever reading the generic message, never
        // the specific errors array the backend returns for password
        // validation failures on this endpoint.
        const specificErrors = err.response?.data?.errors;
        return {
            success: false,
            error: Array.isArray(specificErrors) && specificErrors.length > 0
                ? specificErrors
                : (err.response?.data?.message || err.message),
        }
    }
}

export const addNewChronicCondition = async (obj,token) => {
    try {
        const response = await axios.post(`${API_URL}/users/add-chronic-condition`, obj, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        })
        // console.log('add new chronic condition response: ',response)
        if (response.status >= 200 && response.status < 300) {
            const updatedConditions = response.data
            updatedConditions.forEach((item) => {
                if (item.condition?.toLowerCase() == obj.condition?.toLowerCase()) {
                    return { success: true, data: updatedConditions}
                }
            })
            return { success: false, error: 'This condition already exist'}
        } else {
            return { success: false}
        }
    } catch (err) {
        console.error('Error adding a new chronic condition: ',err)
        return { success: false }
    }
}

export const deleteChronicCondition = async (obj,token) => {
    try {
        const response = await axios.post(`${API_URL}/users/remove-chronic-condition`, obj, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        }) 
        // console.log('Deleting chronic condition response: ', response)
        if (response.status >= 200 && response.status < 300) {
            const updatedConditions = response.data
            updatedConditions.forEach((item) => {
                if (item.condition?.toLowerCase() == obj.condition?.toLowerCase()) {
                    return { success: false}
                }
            })
            return { success: true, data: updatedConditions }
        } else {
            return { success: false}
        }
    } catch (err) {
        console.error('Error deleting a chronic condition: ',err)
        return { success: false}
    }
}

// Settings
export const updateNotificationSettings = async (obj,token) => {
    try {
        const response = await axios.post(`${API_URL}/users/update-notification-settings`, obj, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        }) 
        // console.log('update notification settings response: ',response)
        if (response.status >= 200 && response.status < 300) {
            return { success : true}
        } else {
            return { success: false, error: ""}
        }
    } catch (err) {
        return { success: false, error: err.message}
    }
}


export const getUserSettings = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/users/settings`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
                }
        }) 
        // console.log('user settings response: ', response)
        if (response.status >= 200 && response.status < 300) {
            return response.data
        } else {
            return null
        }
    } catch (err) {
        console.error('Error getting user settings: ',err)
        return null
    }
}

export const updateLanguagePreference = async (language, token) => {
    try {
        const response = await axios.patch(`${API_URL}/users/me/language`, { language }, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        if (response.status >= 200 && response.status < 300) {
            return { success: true, data: response.data }
        }
        return { success: false }
    } catch (err) {
        console.error('Error updating language preference: ', err)
        return { success: false, error: err.message }
    }
}

export const sendMessageContactForm = async (data) => {
    try {
        const response = await axios.post(`${API_URL}/inquiry/contact-form`, data)
        // console.log('User contact form response: ',response)
        if (response.status >= 200 && response.status < 300) {

            return { success: true}
        } 
        return { success: false}
    } catch (err) {
        console.error('Error could complete email: ',err)
        return { success: false}
    }
}
